import React, { useState, useContext, useMemo } from 'react'

import { useFlag } from '@unleash/proxy-client-react'

import { Box, Dropdown, Flex, Icon, Text } from 'stardust'

import styled from 'styled-components'

import { DropdownMenuItem } from '~/components/DropdownMenu'
import { ServiceContext } from '~/contexts/Service'
import * as AIAssistAnalytics from '~/modules/analytics/aiAssistant'
import { aiAssistantEnabled, aiAssistantGenerateLearningEnabled } from '~/modules/permissions'
import { FlexProps } from '~/ui-components/Flex'
import colors from '~/ui-components/tokens/colors'

import { PostContext } from '../PostProvider'

import { AssistActions } from './AssistModal/interface'
import Media from './PostModalMedia'

const S = {
  VerticalLine: styled.div`
    border-left: 2px solid #d3d3d3;
    margin-left: 16px;
    margin-right: 16px;
  `,
  Icon: styled(Icon)`
    border-radius: 50%;
    padding: 10px;
    background-color: ${colors.cosmicShade4};
    &:hover {
      background-color: ${colors.cosmicShade6};
      transition: background-color 0.2s;
    }
  `,
  HighlightedIcon: styled(Icon)`
    border-radius: 50%;
    padding: 10px;
    background-color: ${colors.pulsarPurple16};
    &:hover {
      background-color: ${colors.pulsarPurple15};
      transition: background-color 0.2s;
    }
  `,
  StatusIcon: styled(Icon)`
    position: absolute;
    top: -12px;
    right: -5px;
  `,
  StatusForTagItems: styled(Box)`
    padding: 4px 8px 4px 8px;
    position: absolute;
    top: -12px;
    right: -1px;
  `,
}
interface Props {
  rooms?: Playground.ObservationRoom[]
  taggedChildren: Playground.SimpleChild[]
  learningOutcomes: Playground.LearningFrameworkOutcome[]
  taggedDocuments: Playground.Document[]
  isDescriptionEmpty?: boolean
  onOpenRoomModal: () => void
  onOpenChildrenModal: () => void
  onOpenLinksModal: () => void
  onOpenLearningOutcomesModal: () => void
  onOpenAssistModal: (option: Playground.AssistActions) => void
  setUploadErrors: (error: Playground.Error[]) => void
  setAssistClicked: () => void
}

interface TagIconsProps extends FlexProps {
  iconName: string
  name: string
  size: number
  color?: string
  ml?: number
  mr?: number
  count?: number
}

interface DropdownMenuItemProps {
  iconName: string
  name: string
  size: number
  color?: string
  ml?: number
  mr?: number
  handleClose: () => void
}

const ModalFooter = ({
  rooms,
  taggedChildren,
  learningOutcomes,
  taggedDocuments,
  isDescriptionEmpty,
  onOpenRoomModal,
  onOpenChildrenModal,
  onOpenLinksModal,
  onOpenLearningOutcomesModal,
  onOpenAssistModal,
  setUploadErrors,
  setAssistClicked,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const { state, handlers } = useContext(PostContext)
  const { selectedServiceFeatures } = useContext(ServiceContext)
  const aiAssistFlag = aiAssistantEnabled(selectedServiceFeatures)
  const aiGenerateLearning = aiAssistantGenerateLearningEnabled(selectedServiceFeatures)
  const disableAssistOptions = useFlag('disable_assist_options')

  const StatusTag = ({ count }: { count: number }) => (
    <S.StatusForTagItems borderRadius="16px" alignItems="center" height={22} bg={colors.pulsarPurple16}>
      <Text fontWeight={700} fontSize="14px" caps color="white">
        {count}
      </Text>
    </S.StatusForTagItems>
  )

  const TagIcons = ({ iconName, name, size, color, ml, mr, count }: TagIconsProps) => {
    const handleClick = () => {
      if (name === 'room') {
        onOpenRoomModal()
      }
      if (name === 'childrenAlt') {
        onOpenChildrenModal()
      }
      if (name === 'links') {
        onOpenLinksModal()
      }
      if (name === 'outcomesAlt') {
        onOpenLearningOutcomesModal()
      }
    }
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        ml={ml}
        mr={mr}
        position="relative"
        onClick={handleClick}>
        <S.Icon width={size} name={name} mb={1} color={color} />
        {name === 'room' && rooms && rooms[0]?.roomFkey ? (
          <S.StatusIcon width={20} height={19} name="checkCircleAlt2" />
        ) : count && count > 0 ? (
          <StatusTag count={count} />
        ) : null}
        <Text fontWeight={500} fontSize="14px" caps>
          {iconName}
        </Text>
      </Flex>
    )
  }

  const PopupButton = ({ iconName, name, size, color, ml, mr, handleClose }: DropdownMenuItemProps) => {
    const handleClick = () => {
      handleClose()
      setAssistClicked()
      AIAssistAnalytics.openAssistOptions()
      localStorage.setItem('isAssistClicked', '1')
    }

    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        ml={ml}
        mr={mr}
        position="relative"
        onClick={handleClick}
        data-test={`${iconName.replace(' ', '-').toLowerCase()}-popup-button`}>
        <S.HighlightedIcon width={size} name={name} mb={1} color={color} />
        <Text fontWeight={500} fontSize="14px" caps>
          {iconName}
        </Text>
      </Flex>
    )
  }

  const assistOptions = useMemo(() => {
    const baseItems = [
      {
        label: 'Improve spelling & grammar',
        key: AssistActions.SPELLING,
        onClick: onOpenAssistModal,
        disabled: isDescriptionEmpty,
      },
      // 'Generate learning' will be conditionally inserted here if enabled
      {
        label: 'Expand upon',
        key: AssistActions.EXPAND,
        onClick: onOpenAssistModal,
        disabled: isDescriptionEmpty,
      },
      {
        label: 'Trim content',
        key: AssistActions.TRIM,
        onClick: onOpenAssistModal,
        disabled: isDescriptionEmpty,
      },
      {
        label: 'Change tone',
        key: AssistActions.TONE,
        onClick: onOpenAssistModal,
        disabled: isDescriptionEmpty,
      },
      {
        label: 'Translate',
        key: AssistActions.TRANSLATE,
        onClick: onOpenAssistModal,
        disabled: isDescriptionEmpty,
      },
    ]
    if (aiGenerateLearning) {
      baseItems.splice(1, 0, {
        label: 'Generate learning',
        key: AssistActions.GENERATE,
        onClick: onOpenAssistModal,
        disabled: false,
      })
    }
    return baseItems
  }, [aiGenerateLearning, isDescriptionEmpty, onOpenAssistModal])

  return (
    <Box bg="surfacePrimary" borderRadius={1} borderColor="surfacePrimaryBorder" border="2px solid">
      <Flex justifyContent="space-between" alignItems="center" m={2} pt={1}>
        <Box minWidth="15%">
          <Text ml={2} fontSize={3} fontWeight={'bold'}>
            Add to post
          </Text>
        </Box>
        <Flex flexDirection="row" m={2}>
          <Box>
            <Media
              setUploadErrors={setUploadErrors}
              button={
                <Box cursor="pointer">
                  <TagIcons
                    color={colors.cosmicShade15}
                    size={42}
                    name="attachments"
                    iconName="Attachments"
                  />
                </Box>
              }
              handleUploadedMedia={handlers.uploadedMedia}
              loading={[loading, setLoading]}
              media={state.media}
              uploadedMedia={state.uploadedMedia}
              onChange={handlers.onChange}
            />
          </Box>
          <S.VerticalLine />
          <TagIcons size={40} name="room" iconName="room" ml={1} mr={1} color="black" />
          <TagIcons size={41} name="childrenAlt" iconName="children" ml={3} count={taggedChildren.length} />
          <TagIcons
            size={43}
            name="outcomesAlt"
            iconName="outcomes"
            ml={3}
            mt={2}
            count={learningOutcomes.length}
          />
          <TagIcons size={41} name="links" iconName="links" ml={3} count={taggedDocuments.length} />
          {aiAssistFlag ? (
            <Dropdown
              placement="top-end"
              renderButton={({ handleClose }) => (
                <PopupButton
                  size={41}
                  name="sparkles"
                  iconName="Assist"
                  ml={3}
                  color={colors.cosmicShade0}
                  handleClose={handleClose}
                />
              )}
              render={({ handleClose }) =>
                assistOptions.map((item) => {
                  const onClick = () => {
                    handleClose()
                    item.onClick(item.key)
                  }

                  return (
                    <DropdownMenuItem
                      key={item.key}
                      label={item.label}
                      onClick={onClick}
                      disabled={disableAssistOptions ? item.disabled : false}
                    />
                  )
                })
              }
            />
          ) : null}
        </Flex>
      </Flex>
    </Box>
  )
}
ModalFooter.displayName = 'ModalFooter'

export default React.memo(ModalFooter)
